import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon, FunnelIcon } from "@heroicons/react/16/solid";
import { PaginationComponent } from "@/components/PaginationComponent";
import { useExamPaperList } from "@/apis/exam_papers/getExamPaperList";
import { getPaginationInfo } from "@/types/pagination";
import { Badge } from "@/components/badge";
import { getPaperTypeLabel } from "@/constants/exam";
import { getDifficultyLabel } from "@/utils/exam";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import SortableTableHeader from "@/components/SortableTableHeader";
import ActiveFilters, { type Filter } from "@/components/ActiveFilters";
import FilteringExamPaperListModal, {
  type FilterState,
  type ExamTypeFilter,
} from "./components/FilteringExamPaperListModal";
import { useQueryParameters } from "@/hooks/useQueryParameters";
import type { SubjectType } from "@/apis/exam_progress/types";

type SortField = "title" | "difficulty" | "created_at";
type SortOrder = "asc" | "desc";

const ExamPaperListPage: React.FC = () => {
  const navigate = useNavigate();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  // Configure all query parameters with localStorage persistence
  const { params, setParam, setParams, resetParams } = useQueryParameters({
    page: {
      defaultValue: 1,
      removeWhenDefault: true,
    },
    search: {
      defaultValue: "",
      removeWhenDefault: true,
    },
    examTypeFilter: {
      defaultValue: "all",
      removeWhenDefault: true,
      persist: true,
    },
    typeFilter: {
      defaultValue: "all",
      removeWhenDefault: true,
      persist: true, // Save to localStorage
    },
    difficultyFilter: {
      defaultValue: "all",
      removeWhenDefault: true,
      persist: true, // Save to localStorage
    },
    sort: {
      defaultValue: null as SortField | null,
      removeWhenDefault: true,
      persist: true, // Save to localStorage
    },
    order: {
      defaultValue: "desc" as SortOrder,
      removeWhenDefault: true,
      persist: true, // Save to localStorage
    },
    limit: {
      defaultValue: 10,
      removeWhenDefault: true,
    },
  }, {
    storageKey: "examPaperListSettings", // Storage key for persisted params
  });

  // Sync search input with URL param
  useEffect(() => {
    setSearchInput(params.search);
  }, [params.search]);

  // API call with params
  const { data, isLoading, error } = useExamPaperList({
    page: params.page,
    limit: params.limit,
    title: params.search || undefined,
    exam_type: params.examTypeFilter === "all" ? undefined : params.examTypeFilter,
    subject: params.typeFilter === "all" ? undefined : params.typeFilter,
    difficulty:
      params.difficultyFilter === "all"
        ? undefined
        : parseInt(params.difficultyFilter, 10),
    sort: params.sort || undefined,
    sort_order: params.sort ? params.order : undefined,
  });

  // Search handler
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParams({
      search: searchInput,
      page: 1, // Reset to first page on search
    });
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    if (params.sort === field) {
      if (params.order === "asc") {
        setParam("order", "desc");
      } else {
        // Remove sorting
        setParams({
          sort: null,
          order: "desc",
        });
      }
    } else {
      setParams({
        sort: field,
        order: "asc",
        page: 1, // Reset to first page on sort change
      });
    }
  };

  // Filter handlers
  const handleApplyFilters = (newFilters: FilterState) => {
    setParams({
      examTypeFilter: newFilters.examTypeFilter,
      typeFilter: newFilters.typeFilter,
      difficultyFilter: newFilters.difficultyFilter,
      page: 1,
    });
  };

  const handleRemoveFilter = (filterKey: string) => {
    if (filterKey === "examTypeFilter") {
      setParams({ examTypeFilter: "all", page: 1 });
    } else if (filterKey === "typeFilter") {
      setParams({ typeFilter: "all", page: 1 });
    } else if (filterKey === "difficultyFilter") {
      setParams({ difficultyFilter: "all", page: 1 });
    }
  };

  const handleClearAllFilters = () => {
    setParams({
      examTypeFilter: "all",
      typeFilter: "all",
      difficultyFilter: "all",
      page: 1,
    });
  };

  // Get active filters for display
  const getActiveFilters = (): Filter[] => {
    const activeFilters: Filter[] = [];

    if (params.examTypeFilter !== "all") {
      activeFilters.push({
        key: "examTypeFilter",
        label: `Exam Type: ${params.examTypeFilter}`,
        value: params.examTypeFilter,
      });
    }

    if (params.typeFilter !== "all") {
      activeFilters.push({
        key: "typeFilter",
        label: `Subject: ${getPaperTypeLabel(params.typeFilter)}`,
        value: params.typeFilter,
      });
    }

    if (params.difficultyFilter !== "all") {
      activeFilters.push({
        key: "difficultyFilter",
        label: `Difficulty: Level ${params.difficultyFilter}`,
        value: params.difficultyFilter,
      });
    }

    return activeFilters;
  };

  // Pagination info
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // Page change handler
  const handlePageChange = (page: number) => {
    setParam("page", page);
  };

  // Navigation handlers
  const handleAddExamPaper = () => {
    const examType = params.examTypeFilter !== "all" ? params.examTypeFilter : undefined;
    const query = examType ? `?exam_type=${examType}` : "";
    navigate(`/exam_papers/create${query}`);
  };

  const handleExamPaperClick = (id: string) => {
    navigate(`/exam_papers/${id}`);
  };

  const handlePreviewClick = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/exam_papers/${id}/preview/1`);
  };

  // Get access display
  const getAccessDisplay = (isPrivate?: boolean) => {
    if (isPrivate === true) {
      return <Badge color="zinc">Private</Badge>;
    }
    return <Badge color="lime">Public</Badge>;
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Exam Papers</Heading>
        <Text className="text-zinc-500">
          View all exam papers. Use the search to find specific papers.
        </Text>

        <div className="flex justify-between mt-4">
          <div className="flex items-center gap-3">
            <Button onClick={handleAddExamPaper}>
              <PlusIcon className="h-5 w-5" />
              Add Exam Paper
            </Button>

            <Button onClick={() => setIsFilterModalOpen(true)}>
              <FunnelIcon />
              Filter
            </Button>
          </div>

          <form onSubmit={handleSearch} className="flex items-center">
            <InputGroup>
              <MagnifyingGlassIcon className="h-5 w-5 text-zinc-400" />
              <Input
                name="search"
                placeholder="Search by title..."
                aria-label="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </InputGroup>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        {/* Active Filters */}
        <ActiveFilters
          filters={getActiveFilters()}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <Text>Loading exam papers...</Text>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-8 text-red-600">
            <Text>Failed to load exam papers. Please try again.</Text>
          </div>
        )}

        {/* Exam Papers Table */}
        {!isLoading && !error && (
          <Table>
            <TableHead>
              <TableRow>
                <SortableTableHeader
                  sortKey="title"
                  currentSortField={params.sort}
                  sortOrder={params.order}
                  onSort={handleSort}
                >
                  Title
                </SortableTableHeader>
                <TableHeader>Exam Type</TableHeader>
                <TableHeader>Subject</TableHeader>
                <SortableTableHeader
                  sortKey="difficulty"
                  currentSortField={params.sort}
                  sortOrder={params.order}
                  onSort={handleSort}
                >
                  Difficulty
                </SortableTableHeader>
                <TableHeader>Access</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.items && data.items.length > 0 ? (
                data.items.map((examPaper) => (
                  <TableRow
                    key={examPaper.id}
                    onClick={() => handleExamPaperClick(examPaper.id)}
                    className="cursor-pointer hover:bg-zinc-50"
                  >
                    <TableCell className="font-medium">{examPaper.title}</TableCell>
                    <TableCell>{examPaper.exam_type}</TableCell>
                    <TableCell>{getPaperTypeLabel(examPaper.subject)}</TableCell>
                    <TableCell>{getDifficultyLabel(examPaper.difficulty)}</TableCell>
                    <TableCell>{getAccessDisplay(examPaper.is_private)}</TableCell>
                    <TableCell>
                      <Button
                        onClick={(e: React.MouseEvent) =>
                          handlePreviewClick(examPaper.id, e)
                        }
                        color="white"
                      >
                        Preview
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    No exam papers found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Filter Modal */}
        <FilteringExamPaperListModal
          open={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleApplyFilters}
          currentFilters={{
            examTypeFilter: params.examTypeFilter as ExamTypeFilter,
            typeFilter: params.typeFilter as "all" | SubjectType,
            difficultyFilter: params.difficultyFilter as "all" | "1" | "2" | "3" | "4" | "5",
          }}
        />
      </div>
    </PageContainer>
  );
};

export default ExamPaperListPage;